import { ColorMode, SeverityBucket } from '../types';

// Create a map for the numeric ranges for each severity bucket
const SeverityBucketLookup: Record<number, SeverityBucket> = {};
for (let i = 1; i <= 10; i++) {
  if (i >= 7) {
    SeverityBucketLookup[i] = SeverityBucket.High;
  } else if (i >= 4) {
    SeverityBucketLookup[i] = SeverityBucket.Medium;
  } else {
    SeverityBucketLookup[i] = SeverityBucket.Low;
  }
}

// Map each severity bucket to a color
const SeverityColorMap: Record<SeverityBucket, string> = {
  [SeverityBucket.Low]: 'teal',
  [SeverityBucket.Medium]: 'yellow',
  [SeverityBucket.High]: 'red',
};

// Returns the color string for a severity score segment based on the score and color mode
const getSeverityScoreSegmentFillColor = (
  segmentIndex: number,
  score: number,
  severityBucket: SeverityBucket,
  colorMode: ColorMode,
): string => {
  if (!score || score <= 0) return 'gray';

  const colorVariant =
    segmentIndex < score
      ? '.500'
      : colorMode === ColorMode.Dark
        ? '.800'
        : '.100';

  return `${SeverityColorMap[severityBucket]}${colorVariant}`;
};

// Generates the SVG path data for a segment in the severity score circle
const makeSegmentPath = (segmentIndex: number, totalSegments = 10): string => {
  const cx = 50;
  const cy = 50; // Center of the circle
  const outerRadius = 35;
  const innerRadius = 25; // Radii for the arcs
  const fullAngle = (2 * Math.PI) / totalSegments; // Angle per segment
  const gapAngle = fullAngle * 0.1; // Gap between segments
  const offset = -Math.PI / 2; // Start from top

  const start = segmentIndex * fullAngle + gapAngle / 2 + offset;
  const end = (segmentIndex + 1) * fullAngle - gapAngle / 2 + offset;

  const polarToCartesian = (r: number, a: number) => [
    cx + r * Math.cos(a),
    cy + r * Math.sin(a),
  ];

  const [xOuter1, yOuter1] = polarToCartesian(outerRadius, start);
  const [xOuter2, yOuter2] = polarToCartesian(outerRadius, end);
  const [xInner2, yInner2] = polarToCartesian(innerRadius, end);
  const [xInner1, yInner1] = polarToCartesian(innerRadius, start);

  return `M ${xOuter1} ${yOuter1} A ${outerRadius} ${outerRadius} 0 0 1 ${xOuter2} ${yOuter2} L ${xInner2} ${yInner2} A ${innerRadius} ${innerRadius} 0 0 0 ${xInner1} ${yInner1} Z`;
};

export {
  getSeverityScoreSegmentFillColor,
  makeSegmentPath,
  SeverityBucketLookup,
  SeverityColorMap,
};
